import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import { Auth0Provider } from '@auth0/auth0-react'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { setTokenGetter } from './api/client'
import { setExportsTokenFetcher } from './api/exports'
import { useAuth } from '@/lib/AuthContext'
import * as analytics from '@/lib/analytics'
import App from '@/App.jsx'
import '@/index.css'

if (typeof window !== 'undefined') {
  document.documentElement.lang = 'he';
  document.documentElement.dir = 'rtl';
}

function TokenBridge({ children }) {
  const { isAuthenticated, getToken, user } = useAuth();
  const navigate = useNavigate();
  const didRedirectToSurvey = useRef(false);

  // Set token getter synchronously during render so hasToken() is correct
  // before React Query hooks evaluate their `enabled` flag
  if (isAuthenticated) {
    setTokenGetter(() => getToken(), true);
    setExportsTokenFetcher(() => getToken());
  } else {
    setTokenGetter(() => Promise.resolve(null), false);
    setExportsTokenFetcher(() => Promise.resolve(null));
  }

  // Side effects (referral processing) stay in useEffect
  useEffect(() => {
    if (isAuthenticated) {
      const refCode = sessionStorage.getItem('pending_referral');
      if (refCode) {
        sessionStorage.removeItem('pending_referral');
        getToken().then((token) => {
          fetch('/api/referral', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ referral_code: refCode }),
          }).catch(() => {});
        });
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('m3_auth')) return;

      let isNewSignup = false;
      try {
        const userKey = `mom3ntum_tracked_${user.sub}`;
        const alreadyTracked = localStorage.getItem(userKey);
        const email = user?.email;
        if (alreadyTracked) {
          analytics.trackLogin({ email });
        } else {
          analytics.trackSignup({ email });
          localStorage.setItem(userKey, '1');
          isNewSignup = true;
        }
      } catch {
        // Storage unavailable — skip tracking silently
      }

      // Clean up the URL parameter
      params.delete('m3_auth');
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);

      // Redirect all logins to the survey page
      if (!didRedirectToSurvey.current) {
        didRedirectToSurvey.current = true;
        navigate('/survey', { replace: true });
      }
    }
  }, [isAuthenticated, user]);

  return children;
}

function Auth0ProviderWithNavigate({ children }) {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    const returnTo = appState?.returnTo || '/';
    const separator = returnTo.includes('?') ? '&' : '?';
    navigate(`${returnTo}${separator}m3_auth=1`);
  };

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <BrowserRouter>
    <Auth0ProviderWithNavigate>
      <TokenBridge>
        <App />
      </TokenBridge>
    </Auth0ProviderWithNavigate>
  </BrowserRouter>
)
